import React, { useEffect, useRef, useState } from "react";
import config from "../config";
import Notification from "./Notification";

const Header = ({
  isMobile,
  setIsSidebarOpen,
  onViewProfile,
  onOpenSettings,
  onLogout,
  onOpenCreate,
}) => {
  const menuRef = useRef(null);

  const [admin, setAdmin] = useState(null);
  const [avatar, setAvatar] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);

  /* LOAD ADMIN */
  useEffect(() => {
    const stored = localStorage.getItem("admin");
    if (stored) setAdmin(JSON.parse(stored));
  }, []);

  /* LOAD PROFILE PIC */
  useEffect(() => {
    if (!admin?.username) return;
    const fetchAvatar = async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/Admin/get-profile-picture/${admin.username}`
        );
        if (!res.ok || res.status === 204) return;
        const blob = await res.blob();
        setAvatar(URL.createObjectURL(blob));
      } catch {
        // silent
      }
    };
    fetchAvatar();
  }, [admin?.username]);

  /* CLOSE ON OUTSIDE CLICK */
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const closeMenu = () => setOpenMenu(false);
  const displayName = admin?.fullNames || "Admin";
  const username = admin?.username || "";

  return (
    <div className="bg-gray-800 border-b border-gray-700 sticky top-0 z-20">
      <div className="px-4 py-3 md:px-6 md:py-4">

        {/* TOP ROW */}
        <div className="flex justify-between items-center gap-2">

          {/* LEFT: hamburger + greeting */}
          <div className="flex items-center gap-3 min-w-0">
            {isMobile && (
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 hover:bg-gray-700 rounded-lg flex-shrink-0"
              >
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}
            <div className="min-w-0">
              <h2 className="text-base sm:text-xl md:text-2xl font-bold text-white truncate">
                Welcome back, <span className="text-indigo-400">{displayName}</span>!
              </h2>
              <p className="text-xs text-gray-400 hidden sm:block">Have a great day at work</p>
            </div>
          </div>

          {/* RIGHT: create, notifications, avatar */}
          <div className="flex items-center gap-2 flex-shrink-0">

            {/* Create button */}
            <button
              onClick={onOpenCreate}
              className="hidden sm:flex items-center gap-1 px-3 py-1.5 md:px-4 md:py-2
                bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg shadow transition"
            >
              <span className="text-base leading-none">+</span>
              <span>Create</span>
            </button>

            {/* Notifications */}
            <Notification />

            {/* ── FACEBOOK-STYLE AVATAR + DROPDOWN ── */}
            <div className="relative" ref={menuRef}>

              {/* Trigger button: avatar + name + chevron */}
              <button
                onClick={() => setOpenMenu((v) => !v)}
                className="flex items-center gap-2 px-1 py-1 rounded-full
                  hover:ring-2 hover:ring-indigo-500/40 transition-all duration-200 focus:outline-none"
              >
                {/* Avatar circle */}
                <div className="relative w-9 h-9 flex-shrink-0">
                  <div className="w-full h-full rounded-full overflow-hidden ring-2 ring-gray-600">
                    {avatar ? (
                      <img src={avatar} alt="avatar" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-bold text-white text-sm"
                        style={{ background: "linear-gradient(135deg,#b45309,#d97706)" }}>
                        {displayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  {/* Online dot */}
                  <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-gray-800" />
                </div>

                {/* Name + role label (md+) */}
                <div className="hidden md:flex flex-col items-start leading-none">
                  <span className="text-sm font-semibold text-white max-w-[110px] truncate">{displayName}</span>
                  <span className="text-[10px] text-gray-500 mt-0.5">Administrator</span>
                </div>

                {/* Chevron */}
                <svg className={`w-3.5 h-3.5 text-gray-500 hidden md:block transition-transform duration-200 ${openMenu ? "rotate-180" : ""}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* ── DROPDOWN ── */}
              {openMenu && (
                <div className="absolute right-0 mt-2 w-72 z-[999] rounded-2xl overflow-hidden"
                  style={{
                    background: "#111827",
                    border: "1px solid rgba(255,255,255,0.08)",
                    boxShadow: "0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)",
                  }}>

                  {/* Cover + Big Avatar */}
                  <div className="relative h-20"
                    style={{ background: "linear-gradient(135deg,#92400e 0%,#b45309 50%,#d97706 100%)" }}>
                    {/* Role badge */}
                    <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full border
                      bg-yellow-500/20 text-yellow-300 border-yellow-500/40">
                      🛡️ Admin
                    </span>
                    {/* Big avatar */}
                    <div className="absolute -bottom-8 left-4">
                      <div className="w-16 h-16 rounded-full border-4 border-gray-900 overflow-hidden">
                        {avatar ? (
                          <img src={avatar} alt="avatar" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white text-2xl font-bold"
                            style={{ background: "linear-gradient(135deg,#b45309,#d97706)" }}>
                            {displayName.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      {/* Online dot */}
                      <span className="absolute bottom-1 right-1 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-gray-900" />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="pt-10 px-4 pb-4">
                    <div className="mb-3">
                      <h3 className="text-white font-bold text-sm leading-tight truncate">{displayName}</h3>
                      <p className="text-gray-500 text-xs mt-0.5">@{username}</p>
                    </div>

                    <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
                      <svg className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>Administrator</span>
                    </div>

                    <div className="border-t border-gray-700/60 mb-3" />

                    {/* Action buttons */}
                    <div className="space-y-0.5">
                      <button
                        onClick={() => { closeMenu(); onViewProfile(); }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-gray-300 hover:bg-gray-700/80 hover:text-white transition-colors text-left">
                        <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0 border border-gray-700">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <span className="font-medium">View Profile</span>
                      </button>

                      <button
                        onClick={() => { closeMenu(); onOpenSettings(); }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-gray-300 hover:bg-gray-700/80 hover:text-white transition-colors text-left">
                        <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0 border border-gray-700">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <span className="font-medium">Settings & Privacy</span>
                      </button>

                      {isMobile && (
                        <button
                          onClick={() => { closeMenu(); onOpenCreate(); }}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-gray-300 hover:bg-gray-700/80 hover:text-white transition-colors text-left">
                          <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0 border border-gray-700">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </div>
                          <span className="font-medium">+ Create</span>
                        </button>
                      )}

                      <button
                        onClick={() => { closeMenu(); onLogout(); }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-red-400 hover:bg-red-500/10 transition-colors text-left">
                        <div className="w-8 h-8 bg-red-500/10 rounded-full flex items-center justify-center flex-shrink-0 border border-red-500/20">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                        </div>
                        <span className="font-medium">Log Out</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* ── END AVATAR DROPDOWN ── */}

          </div>
        </div>

        {/* BOTTOM ROW — full-width Create on xs only */}
        <div className="mt-2 sm:hidden">
          <button
            onClick={onOpenCreate}
            className="w-full flex items-center justify-center gap-1 px-4 py-2
              bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg shadow transition"
          >
            <span className="text-base leading-none">+</span>
            <span>Create</span>
          </button>
        </div>

      </div>
    </div>
  );
};

export default Header;